import { useCallback, useEffect } from 'react';
import debounce from 'lodash/debounce';
import useCustomHook from '@/hooks/useCustomHook';
import { hasValue } from '@/modules/numberUtils';
import { getComicPageCues } from './getComicCues';
import useMaximizeScreen from '@/shared/Activities/ActivityComponents/MaximizeScreen/useMaximizeScreen.hook';

export const PLAY_BUBBLE_END_DELAY = 0.25;
export default function useComicPlayer({
  pages,
  currentPageId,
  initialCue,
  comicImageRef,
  onStart,
  onRestart,
  onProgress,
  onPageChange,
  detectedCuesRef,
  readAudioRef,
}) {
  const { maximize, minimize } = useMaximizeScreen();

  const { state, actions } = useCustomHook({
    debug: true,
    defaultState: {
      imgNaturalSize: null,
      imgPixelSize: null,
      currentPage: null,
      currentBox: null,
      currentBubble: null,
      currentCue: null,
      bubbleCues: null,
      pageCues: null,
      seekTo: null,
      playing: false,
      bubbleTime: 0,
      pageImageLoaded: false,
      playerReady: false,
      pageReady: false,
      started: false,
      initialCueProcessed: false,
      endOfPageReached: false,
      maxCueId: '0_00000',
      readMode: true,
      comingFromPageId: null,
      animationsReady: false,
      restartAnimations: false,
    },
    actions: {
      onPlayerReady: () => {
        actions.setState({ playerReady: true });
      },
      onPageImageLoaded: ({ naturalWidth, naturalHeight }) => {
        actions.setState({
          pageImageLoaded: true,
          imgNaturalSize: {
            width: naturalWidth,
            height: naturalHeight,
          },
        });
      },
      onAnimationsReady: () => {
        actions.setState({ animationsReady: true });
      },
      playPause: () => {
        if (state.endOfPageReached) {
          actions.setState({
            playing: !state.playing,
            readMode: state.playing,
            showBubbleExplanation: false,
            endOfPageReached: false,
            currentBox: null,
            currentBubble: null,
            currentCue: null,
            restartAnimations: true,
          });
        } else {
          const newPlaying = !state.playing;
          actions.setState({
            playing: newPlaying,
            readMode: !newPlaying,
            showBubbleExplanation: false,
          });
        }
      },
      onComicAudioStart: () => {
        if (onStart && !state.started) onStart();
        actions.setState({ started: true });
      },
      onCue: (detectedCues) => {
        detectedCuesRef.current = detectedCues;
        for (const cue of detectedCues) {
          switch (cue.type) {
            case 'page':
              if (cue.status == 'STARTING') {
                actions.setState({
                  currentPage: pages[cue.pageId],
                  currentBox: null,
                  currentBubble: null,
                  currentCue: cue,
                });
              } else {
                actions.onPageAudioEnd();
              }
              if (onProgress) onProgress(cue, state.pageCues);
              break;
            case 'box':
              if (cue.status == 'STARTING') {
                actions.setState({
                  currentBox: pages[cue.pageId].boxes[cue.boxId],
                  currentCue: cue,
                });
              }
              if (onProgress) onProgress(cue, state.pageCues);

              break;
            case 'bubble':
              if (cue.status == 'STARTING') {
                actions.setState({
                  currentBox: pages[cue.pageId].boxes[cue.boxId],
                  currentBubble:
                    pages[cue.pageId].boxes[cue.boxId].bubbles[cue.bubbleId],
                  currentCue: cue,
                });
              }
              if (onProgress) onProgress(cue, state.pageCues);
              break;
            default:
              break;
          }
        }
      },
      onPageAudioEnd: () => {
        actions.setState({
          //playing: false,
          endOfPageReached: true,
          showBubbleExplanation: false,
          //readMode: true,
          //seekTo: 0,
        });
      },
      onBubblePress: (bubbleCue) => {
        const box = pages[bubbleCue.pageId].boxes[bubbleCue.boxId];
        const bubbleToPlay = box.bubbles[bubbleCue.bubbleId];

        const bubbleEndTime = bubbleToPlay.phrase.end + PLAY_BUBBLE_END_DELAY;
        playAudioBubble(readAudioRef, bubbleToPlay.phrase.start, bubbleEndTime);

        const newState = {
          currentCue: bubbleCue,
          playing: false,
          currentBox: box,
          currentBubble: bubbleToPlay,
          showBubbleExplanation: true,
          readMode: true,
        };
        if (!state.currentPage?.interactions) {
          newState.seekTo = bubbleEndTime;
        }
        console.log(
          '[onBubblePress]',
          state.currentPage?.interactions,
          newState.seekTo
        );
        actions.setState(newState);
        maximize();
      },
      onBubbleExplanationClose: () => {
        actions.setState({ showBubbleExplanation: false });
      },
      onSeek: (time) => {
        actions.setState({ seekTo: null });
      },
      exit: () => {
        minimize();
        actions.setState({ playing: false, showBubbleExplanation: false });
      },
      nextPage: () => {
        detectedCuesRef.current = [];
        const nextPageId =
          state.currentPage?.nextPageId ?? state.currentPage.id + 1;
        const nextPage = pages[nextPageId];
        if (onPageChange) onPageChange(nextPage.id);

        maximize();
        const shouldKeepPlaying = state.playing || state.endOfPageReached;

        actions.setState({
          currentPage: nextPage,
          currentBox: null,
          currentBubble: null,
          currentCue: null,
          playing: shouldKeepPlaying,
          seekTo: 0,
          playerReady: false,
          pageImageLoaded: false,
          animationsReady: false,
          restartAnimations: true,
          pageReady: false,
          readMode: !shouldKeepPlaying,
          endOfPageReached: false,
          comingFromPageId: state.currentPage.id,
        });
      },
      prevPage: () => {
        detectedCuesRef.current = [];
        const prevPageId = state.comingFromPageId ?? state.currentPage.id - 1;
        const prevPage = pages[prevPageId];
        if (onPageChange) onPageChange(prevPage.id);

        maximize();

        actions.setState({
          currentPage: prevPage,
          currentBox: null,
          currentBubble: null,
          playing: false,
          readMode: true,
          seekTo: 0,
          playerReady: false,
          pageImageLoaded: false,
          animationsReady: false,
          pageReady: false,
          endOfPageReached: false,
          comingFromPageId: null,
        });
      },
      restart: () => {
        detectedCuesRef.current = [];
        actions.setState({
          currentPage: pages[0],
          currentBox: null,
          currenBubble: null,
          currentCue: null,
          bubbleCues: null,
          pageCues: null,
          seekTo: 0,
          playing: false,
          bubbleTime: 0,
          playerReady: false,
          pageImageLoaded: false,
          animationsReady: false,
          pageReady: false,
          started: false,
          endOfPageReached: false,
          maxCueId: '0_00000',
        });
        if (onRestart) onRestart();
      },
    },
  });

  // Set pageReady when all assets are ready
  useEffect(() => {
    if (
      state.playerReady &&
      state.pageImageLoaded &&
      (state.animationsReady || !state.currentPage.animations)
    ) {
      actions.setState({ pageReady: true });
    } else {
      actions.setState({ pageReady: false });
    }
  }, [
    state.playerReady,
    state.pageImageLoaded,
    state.currentPage?.animations,
    state.animationsReady,
  ]);

  // Initialize cues on new page
  useEffect(() => {
    if (state.currentPage) {
      detectedCuesRef.current = [];
      const pageCues = getComicPageCues(state.currentPage);
      const currentCue = findCurrentCue(
        state.currentPage.id,
        null,
        null,
        pageCues
      );

      const bubbleCues = pageCues.filter((cue) => cue.type === 'bubble');
      actions.setState({
        pageCues,
        bubbleCues,
        currentCue,
      });
    }
  }, [state.currentPage]);

  // Seek to initialCue when continuing from a previous play.
  useEffect(() => {
    if (initialCue && !state.initialCueProcessed && state.pageCues?.length) {
      const { pageId } = initialCue;
      const pageCue = state.pageCues[0];
      if (pageCue.pageId == pageId) {
        actions.setState({
          initialCueProcessed: true,
          seekTo: initialCue.start,
        });
        actions.onCue([
          {
            ...initialCue,
            status: 'STARTING',
          },
        ]);
      } else {
        actions.setState({ currentPage: pages[pageId] });
      }
    }
  }, [state.pageCues, initialCue]);

  // Set play mode when playing the page or a single bubble
  /* useEffect(() => {
    if (state.playing || state.showBubbleExplanation) {
      actions.setState({ readMode: false });
    } else {
      actions.setState({ readMode: true });
    }
  }, [state.playing, state.showBubbleExplanation]); */

  // Update maxCueId so bubbles allready played can be interacted with
  useEffect(() => {
    if (state.currentCue?.id > state.maxCueId) {
      actions.setState({ maxCueId: state.currentCue.id });
    }
  }, [state.currentCue]);

  useEffect(() => {
    if (state.playing) {
      maximize();
      if (state.restartAnimations) {
        actions.setState({ restartAnimations: false });
      }
    }
  }, [state.playing]);

  // Sync page to currrentPageId (sent from editor)
  useEffect(() => {
    const currentPage = hasValue(currentPageId)
      ? pages[currentPageId]
      : pages[0];
    actions.setState({
      currentPage,
    });
  }, [pages, currentPageId]);

  // Responsive sizing
  useEffect(() => {
    let resizeObserver;
    if (comicImageRef.current) {
      updateImgPixelSize();
      resizeObserver = new ResizeObserver(updateImgPixelSize);
      resizeObserver.observe(comicImageRef.current);
    }
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      updateImgPixelSize.cancel(); // Cancel any pending debounced calls
    };
  }, [comicImageRef.current]);

  const updateImgPixelSize = useCallback(
    debounce(() => {
      if (comicImageRef.current) {
        const { width, height } = comicImageRef.current.getBoundingClientRect();
        actions.setState({ imgPixelSize: { width, height } });
      }
    }, 100),
    [comicImageRef.current]
  );

  return {
    state,
    actions,
  };
}

function findCurrentCue(pageId, boxId, bubbleId, cues) {
  return cues.find((cue) => {
    return (
      cue.pageId == pageId &&
      (hasValue(boxId) ? cue.boxId == boxId : true) &&
      (hasValue(bubbleId) ? cue.bubbleId == bubbleId : true)
    );
  });
}

let checkTime = null;
function playAudioBubble(readAudioRef, startTime, endTime) {
  const audio = readAudioRef.current;
  audio.currentTime = startTime - 0.1;

  if (checkTime) {
    clearInterval(checkTime);
  }
  audio.play();

  checkTime = setInterval(() => {
    if (audio.currentTime >= endTime) {
      audio.pause();
      clearInterval(checkTime);
    }
  }, 50);
}
